// ==================================================================================
// Digital Approval System (DAS) - Workflow Summary Dashboard (U6)
// ==================================================================================
// All-time snapshot of the user's position in the system. Two sections:
//   1. Workflow Participation — 4 KPIs (1a/1b/1d/1c) + 2 charts.
//   2. My Submissions — 2 KPIs (2a/2b) + 1 donut.
// ==================================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EChartsCoreOption } from 'echarts/core';
import IconSystem from 'icon-collection/icon-systems';
import ReportPageLayout from '../ReportPageLayout';
import ChartCard from '../ChartCard';
import { EChart } from '../EChart';
import { useUserWorkflowSummary } from 'hooks/api/das/reports';
import { exportToExcel } from 'utils/exportToExcel';
import { useAuth } from 'hooks/api';
import { hasAction } from 'config/rolePermissions';
import { statusColor } from '../statusColors';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const NARROW_BREAKPOINT_PX = 900;

// "Pending by Request Type" caps at top N by total pending — past ~12 types
// the grouped bars lose readable space. Long tail is reachable via the
// "Show all in chart" toggle and the searchable table below.
const CHART_TOP_N = 10;

// ---------------------------------------------------------------------------
// View mode for the "Pending by Request Type" card (2-way segmented control)
// ---------------------------------------------------------------------------
type PendingViewMode = 'chart' | 'blocker';

// Request-type colour palette for the "Pending Value by Request Type" donut.
// 9 distinct, high-contrast colours; the 9th is reserved for the "Others" rollup.
const TYPE_VALUE_PALETTE: string[] = [
  '#2563eb', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#a855f7', // purple
  '#06b6d4', // cyan
  '#f43f5e', // rose
  '#84cc16', // lime
  '#f97316', // orange
  '#94a3b8', // slate — reserved for "Others"
];

// Top-N cap before rolling remaining types into a single "Others" slice.
const VALUE_DONUT_TOP_N = 8;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const sectionHeadingStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#1f2937',
  margin: '0.25rem 0 0.5rem 0',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const kpiRowStyleFactory = (cols: number, isNarrow: boolean): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: isNarrow ? '1fr 1fr' : `repeat(${cols}, minmax(0, 1fr))`,
  gap: '0.75rem',
  // minmax(0, 1fr) is critical: with plain `1fr`, grid items default to their
  // content's min-content width, so a long KPI value can push the grid wider
  // than the container and trigger horizontal page scroll.
});

// Each KPI has its own semantic tone so the row reads at a glance and the
// card colours match the chart series they belong to (e.g. "Awaiting My
// Action" amber matches the donut's amber slice).
type CardTone = 'blue' | 'amber' | 'purple' | 'green' | 'cyan' | 'rose';

const cardTones: Record<
  CardTone,
  { background: string; accent: string; value: string; border: string; label: string }
> = {
  blue: {
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    accent: '#2563eb',
    value: '#1e3a8a',
    border: '#bfdbfe',
    label: '#1e40af',
  },
  amber: {
    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    accent: '#f59e0b',
    value: '#92400e',
    border: '#fde68a',
    label: '#b45309',
  },
  purple: {
    background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
    accent: '#a855f7',
    value: '#6b21a8',
    border: '#e9d5ff',
    label: '#7e22ce',
  },
  green: {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    accent: '#10b981',
    value: '#065f46',
    border: '#bbf7d0',
    label: '#047857',
  },
  cyan: {
    background: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
    accent: '#06b6d4',
    value: '#155e75',
    border: '#a5f3fc',
    label: '#0e7490',
  },
  rose: {
    background: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
    accent: '#f43f5e',
    value: '#9f1239',
    border: '#fecdd3',
    label: '#be123c',
  },
};

const kpiCardStyle: React.CSSProperties = {
  borderRadius: '0.625rem',
  padding: '1rem 1.1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.2rem',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: '1.8rem',
  fontWeight: 800,
  lineHeight: 1.1,
};

const kpiHintStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  opacity: 0.75,
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '320px',
  height: '32px',
  padding: '0 0.625rem',
  fontSize: '0.85rem',
  border: '1px solid #e5e7eb',
  borderRadius: '0.375rem',
  background: '#ffffff',
  color: '#1f2937',
  outline: 'none',
};

const toggleLabelStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  color: '#374151',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  cursor: 'pointer',
  userSelect: 'none',
};

const chartSubtitleStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  flexWrap: 'wrap',
};

// ---------------------------------------------------------------------------
// Segmented control for the "Pending by Request Type" view toggle
// ---------------------------------------------------------------------------
const segmentedControlStyle: React.CSSProperties = {
  display: 'inline-flex',
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  overflow: 'hidden',
  background: '#f9fafb',
};

const segmentBaseStyle: React.CSSProperties = {
  padding: '0.25rem 0.75rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  background: 'transparent',
  color: '#374151',
  border: 'none',
  borderRight: '1px solid #d1d5db',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  outline: 'none',
};

const segmentActiveStyle: React.CSSProperties = {
  background: '#2563eb',
  color: '#ffffff',
};

const subtitleRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '0.75rem',
  flexWrap: 'wrap',
  marginTop: '0.25rem',
};

// ---------------------------------------------------------------------------
// Row styles for the grouped expandable list (used by submitter + blocker views)
// ---------------------------------------------------------------------------
const groupOuterRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1.5rem 1fr auto auto',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid #f3f4f6',
  background: '#fafafa',
  cursor: 'pointer',
  userSelect: 'none',
  fontSize: '0.85rem',
  color: '#1f2937',
  fontWeight: 600,
};

const groupInnerRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1.5rem 1fr auto auto',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.4rem 0.75rem 0.4rem 2.25rem',
  borderBottom: '1px solid #f3f4f6',
  fontSize: '0.82rem',
  color: '#374151',
};

const groupCountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'right',
  minWidth: '2.5rem',
};

const groupValueStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'right',
  color: '#6b7280',
  fontSize: '0.78rem',
  minWidth: '7rem',
};

// ---------------------------------------------------------------------------
// "View all types" link + modal styles (Workflow Value by Request Type donut)
// ---------------------------------------------------------------------------
const viewAllLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '0',
  color: '#2563eb',
  fontSize: '0.78rem',
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
  whiteSpace: 'nowrap',
};

const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
};

const modalContainerStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: '0.625rem',
  width: '100%',
  maxWidth: '640px',
  maxHeight: '85vh',
  zIndex: 1001,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 24px 48px rgba(15, 23, 42, 0.24)',
  overflow: 'hidden',
};

const modalHeaderStyle: React.CSSProperties = {
  padding: '0.875rem 1.25rem',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
};

const modalCloseButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '1.25rem',
  lineHeight: 1,
  cursor: 'pointer',
  color: '#6b7280',
  padding: '0.25rem 0.5rem',
};

const modalBodyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
  padding: '0.5rem 0',
};

const modalRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1.5rem 1fr auto auto',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.5rem 1.25rem',
  borderBottom: '1px solid #f3f4f6',
  fontSize: '0.85rem',
  color: '#1f2937',
};

const modalRowSwatchStyle: React.CSSProperties = {
  width: '0.75rem',
  height: '0.75rem',
  borderRadius: '0.2rem',
  flexShrink: 0,
};

const modalValueStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'right',
  minWidth: '6rem',
};

const modalPctStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'right',
  color: '#6b7280',
  minWidth: '3.5rem',
};

// ---------------------------------------------------------------------------
// Custom HTML legend for the Workflow Value donut (replaces ECharts' default
// legend, which couldn't keep aligned columns when type names varied in width).
// ---------------------------------------------------------------------------
const legendGridStyleFactory = (isNarrow: boolean): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: isNarrow ? '1fr 1fr' : 'repeat(3, minmax(0, 1fr))',
  gap: '0.25rem 0.75rem',
  paddingTop: '0.5rem',
  maxHeight: '6.5rem',
  overflowY: 'auto',
  borderTop: '1px solid #f3f4f6',
});

const legendItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: '0.72rem',
  color: '#374151',
  overflow: 'hidden',
  minWidth: 0,
};

const legendSwatchStyle: React.CSSProperties = {
  width: '0.625rem',
  height: '0.625rem',
  borderRadius: '0.15rem',
  flexShrink: 0,
};

const legendLabelStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
};

// ---------------------------------------------------------------------------
// Vertical side-legend used by the wide single-column donut in Section 2
// (Submissions by Status). Uses CSS grid so swatch/name/count/% align in
// neat columns regardless of label widths.
// ---------------------------------------------------------------------------
const sideLegendWrapStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
  alignItems: 'center',
  columnGap: '0.625rem',
  rowGap: '0.4rem',
  fontSize: '0.82rem',
  color: '#1f2937',
  alignContent: 'center',
  overflowY: 'auto',
  maxHeight: '100%',
};

const sideLegendSwatchStyle: React.CSSProperties = {
  width: '0.7rem',
  height: '0.7rem',
  borderRadius: '0.18rem',
  flexShrink: 0,
};

const sideLegendNameStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
};

const sideLegendCountStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'right',
  minWidth: '2.5rem',
  fontWeight: 600,
};

const sideLegendPctStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'right',
  color: '#6b7280',
  minWidth: '3rem',
  fontSize: '0.76rem',
};

// ---------------------------------------------------------------------------
// Loading state shown while the (multi-query) workflow summary payload is
// still being assembled. Prevents the user from seeing partially-empty
// sections during the initial fetch.
// ---------------------------------------------------------------------------
const loadingStateStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem',
  padding: '5rem 1.5rem',
  minHeight: '360px',
  flex: 1,
  textAlign: 'center',
};

const loadingSpinnerWrapStyle: React.CSSProperties = {
  color: '#2563eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const loadingTitleStyle: React.CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: '#1f2937',
};

const loadingSubtitleStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  color: '#6b7280',
  maxWidth: '26rem',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const WorkflowSummaryReport: React.FC = () => {
  const { data, loading, error, refetch } = useUserWorkflowSummary();

  // Configuration-driven action gate. `export` on `report-my-workflow-summary`
  // gates the Export button rendered by ReportPageLayout. Passing
  // `onExport={undefined}` to the layout hides the button entirely.
  const { user } = useAuth();
  const canExport = useMemo(
    () => hasAction('report-my-workflow-summary', 'export', user?.rolePermissions),
    [user]
  );

  // Viewport-based responsive flag
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${NARROW_BREAKPOINT_PX}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${NARROW_BREAKPOINT_PX}px)`);
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const pendingRows = data?.pendingByRequestType ?? [];
  const statusRows  = data?.submissionsByStatus ?? [];
  const blockerRows = data?.inWorkflowByBlockingApprover ?? [];

  // ----- View toggle for the "Pending by Request Type" card -----
  const [pendingViewMode, setPendingViewMode] = useState<PendingViewMode>('chart');

  // ----- Chart top-N toggle (Pending by Request Type) -----
  const [showAllInChart, setShowAllInChart] = useState(false);

  // ----- "View all types" modal for the Workflow Value donut -----
  const [allTypesModalOpen, setAllTypesModalOpen] = useState(false);

  // Close the modal on ESC.
  useEffect(() => {
    if (!allTypesModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAllTypesModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [allTypesModalOpen]);

  // ----- Top-N chart slice (sorted by InWorkflow desc) -----
  const pendingByTotalDesc = useMemo(
    () => pendingRows.slice().sort((a, b) => b.inWorkflowCount - a.inWorkflowCount),
    [pendingRows]
  );
  const chartPendingRows = useMemo(
    () => (showAllInChart ? pendingByTotalDesc : pendingByTotalDesc.slice(0, CHART_TOP_N)),
    [pendingByTotalDesc, showAllInChart]
  );

  // ====================================================================
  // Section 1 chart: Pending by Request Type — grouped horizontal bar
  // (top N by InWorkflowCount; full list available in the table below)
  // ====================================================================
  const pendingByTypeOption: EChartsCoreOption = useMemo(() => {
    const names = chartPendingRows.map((r) => formatTypeName(r.requestTypeName));
    // At narrow widths the 3 legend items don't fit on a single line and the
    // last one gets clipped against the card edge. Split into two stacked
    // legend instances so each row fits, and push the grid down to compensate.
    const legendCfg: any = isNarrow
      ? [
          { data: ['In Workflow', 'Awaiting My Action'], top: 0, left: 'center', type: 'plain', itemGap: 14 },
          { data: ['As Final Approver'], top: 22, left: 'center', type: 'plain' },
        ]
      : { data: ['In Workflow', 'Awaiting My Action', 'As Final Approver'], top: 0 };
    const gridTop = isNarrow ? 56 : 36;
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: legendCfg,
      grid: { left: 10, right: 30, top: gridTop, bottom: 10, containLabel: true },
      xAxis: { type: 'value', minInterval: 1 },
      yAxis: {
        type: 'category',
        inverse: true,
        data: names,
        axisLabel: { width: 140, overflow: 'truncate' },
      },
      series: [
        {
          name: 'In Workflow',
          type: 'bar',
          data: chartPendingRows.map((r) => r.inWorkflowCount),
          itemStyle: { color: '#2563eb' },
        },
        {
          name: 'Awaiting My Action',
          type: 'bar',
          data: chartPendingRows.map((r) => r.awaitingMyActionCount),
          itemStyle: { color: '#f59e0b' },
        },
        {
          name: 'As Final Approver',
          type: 'bar',
          data: chartPendingRows.map((r) => r.finalApproverCount),
          itemStyle: { color: '#a855f7' },
        },
      ],
    };
  }, [chartPendingRows, isNarrow]);

  // ====================================================================
  // Section 1 chart: Workflow Value by Request Type — donut
  // (scope = every request where I'm in the chain, any state; summed by type.
  //  Top N types + "Others" rollup to keep the donut readable;
  //  "Others" tooltip lists its rolled-up types so the long tail is reachable.)
  // ====================================================================
  const valueRows = data?.workflowValueByRequestType ?? [];

  // Compute the dominant currency across rows (used for the formatter +
  // centre-label). Null when rows disagree or all currencies are NULL.
  const valueDominantCurrency = useMemo<string | null>(() => {
    const set = new Set<string>();
    for (const r of valueRows) if (r.currency) set.add(r.currency);
    return set.size === 1 ? Array.from(set)[0] : null;
  }, [valueRows]);

  const valueTotal = useMemo(
    () => valueRows.reduce((s, r) => s + r.totalValue, 0),
    [valueRows]
  );

  // Hoisted slices — the donut option AND the custom HTML legend both consume
  // these. Names are normalised via formatTypeName so mixed-casing in the
  // source table doesn't show as a jarring mix of ALL CAPS and Title Case.
  const valueSlices = useMemo(() => {
    if (valueRows.length === 0) return [] as Array<{ name: string; value: number; color: string }>;
    const sorted = valueRows.slice().sort((a, b) => b.totalValue - a.totalValue);
    const top = sorted.slice(0, VALUE_DONUT_TOP_N);
    const tail = sorted.slice(VALUE_DONUT_TOP_N);
    const slices = top.map((r, i) => ({
      name: formatTypeName(r.requestTypeName),
      value: r.totalValue,
      color: TYPE_VALUE_PALETTE[i % TYPE_VALUE_PALETTE.length],
    }));
    if (tail.length > 0) {
      slices.push({
        name: `Others (${tail.length})`,
        value: tail.reduce((s, r) => s + r.totalValue, 0),
        color: TYPE_VALUE_PALETTE[slices.length % TYPE_VALUE_PALETTE.length],
      });
    }
    return slices;
  }, [valueRows]);

  // Rolled-up tail, used by the Others-slice tooltip formatter.
  const valueTail = useMemo(
    () =>
      valueRows.slice().sort((a, b) => b.totalValue - a.totalValue).slice(VALUE_DONUT_TOP_N),
    [valueRows]
  );

  const workflowValueByTypeOption: EChartsCoreOption = useMemo(() => {
    if (valueSlices.length === 0) return {};

    // Preview list for the Others tooltip — cap so it doesn't overflow at 30+ types.
    const TAIL_PREVIEW_LIMIT = 10;
    const tailPreview = valueTail.slice(0, TAIL_PREVIEW_LIMIT);
    const tailExtra = valueTail.length - tailPreview.length;

    // No ECharts built-in legend — a custom HTML legend is rendered below the
    // donut for full control over alignment and overflow. Donut therefore fills
    // its container vertically; centre it cleanly at 50% with a generous ring.
    const centerYPercent = 50;
    const outerRadius = '78%';
    const innerRadius = '48%';

    const currencyLabel = valueDominantCurrency ?? '';

    return {
      tooltip: {
        trigger: 'item',
        // Render the tooltip into document.body so it can extend outside the
        // chart card without being clipped by `overflow` on parent elements.
        appendToBody: true,
        // Strip ECharts' default styling so our extraCssText drives everything
        // (font, border, padding, background).
        backgroundColor: 'transparent',
        borderWidth: 0,
        padding: 0,
        textStyle: { color: '#1f2937' },
        // Bound the tooltip's size and let it scroll internally for the
        // "Others" hover-preview when the rolled-up list is long.
        extraCssText:
          'max-width: 360px; max-height: 320px; overflow-y: auto; ' +
          'white-space: normal; word-break: normal; ' +
          'background: #ffffff; border: 1px solid #e5e7eb; ' +
          'border-radius: 0.5rem; padding: 0.625rem 0.75rem; ' +
          'box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18); ' +
          'font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; ' +
          'font-size: 0.82rem; line-height: 1.45; color: #1f2937;',
        // When hovering the "Others (N)" slice, expand the tooltip to list
        // every rolled-up type and its value — gives users the long-tail
        // detail without bloating the legend. For a complete, scrollable
        // view of every type, the "View all" link in the subtitle opens a
        // modal (the tooltip is for at-a-glance preview only).
        formatter: (p: any) => {
          const titleHtml =
            `<div style="font-size: 0.9rem; font-weight: 700; color: #1f2937; margin-bottom: 0.125rem;">` +
            `${p.name}</div>`;
          const valueHtml =
            `<div style="font-size: 0.82rem; color: #374151; margin-bottom: 0.4rem;">` +
            `${formatValueAbbr(p.value)} ${currencyLabel} ` +
            `<span style="color: #9ca3af;">(${p.percent}%)</span></div>`;

          if (valueTail.length > 0 && typeof p.name === 'string' && p.name.startsWith('Others (')) {
            const includesHeader =
              `<div style="font-size: 0.7rem; font-weight: 700; color: #6b7280; ` +
              `text-transform: uppercase; letter-spacing: 0.05em; ` +
              `padding-top: 0.4rem; margin-bottom: 0.25rem; ` +
              `border-top: 1px solid #e5e7eb;">Includes</div>`;
            const items = tailPreview
              .map(
                (r) =>
                  `<div style="display: flex; justify-content: space-between; ` +
                  `gap: 1rem; padding: 0.15rem 0; font-size: 0.78rem;">` +
                  `<span style="color: #1f2937;">${formatTypeName(r.requestTypeName)}</span>` +
                  `<span style="font-variant-numeric: tabular-nums; color: #6b7280; ` +
                  `flex-shrink: 0;">${formatValueAbbr(r.totalValue)}</span>` +
                  `</div>`
              )
              .join('');
            const moreNote =
              tailExtra > 0
                ? `<div style="color: #9ca3af; font-style: italic; ` +
                  `font-size: 0.72rem; padding-top: 0.25rem;">` +
                  `…and ${tailExtra} more — open <b>View all</b> for the complete list</div>`
                : '';
            return titleHtml + valueHtml + includesHeader + items + moreNote;
          }
          return titleHtml + valueHtml;
        },
      },
      // Centre text: total value across all types.
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: `${centerYPercent - 3}%`,
          style: {
            text: formatValueAbbr(valueTotal),
            fontSize: 18,
            fontWeight: 'bold',
            fill: '#1f2937',
            textAlign: 'center',
          },
        },
        {
          type: 'text',
          left: 'center',
          top: `${centerYPercent + 3}%`,
          style: {
            text: currencyLabel ? `${currencyLabel} total` : 'total (mixed)',
            fontSize: 11,
            fill: '#6b7280',
            textAlign: 'center',
          },
        },
      ],
      series: [
        {
          type: 'pie',
          radius: [innerRadius, outerRadius],
          center: ['50%', `${centerYPercent}%`],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
          label: { show: false },
          labelLine: { show: false },
          emphasis: {
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              fontWeight: 700,
              color: '#ffffff',
              formatter: (p: any) => `${formatValueAbbr(p.value)}\n${p.percent}%`,
            },
          },
          data: valueSlices.map((s) => ({
            name: s.name,
            value: s.value,
            itemStyle: { color: s.color },
          })),
        },
      ],
    };
  }, [valueSlices, valueTail, valueDominantCurrency, valueTotal]);

  const hasValueData = valueRows.length > 0 && valueTotal > 0;

  // ====================================================================
  // Section 2 chart: Submissions by Status — donut + side legend
  // ====================================================================
  // Hoisted slices: donut data AND the vertical HTML legend share these.
  const statusSlices = useMemo(
    () =>
      statusRows.map((r) => ({
        name: r.status,
        value: r.count,
        color: statusColor(r.status),
      })),
    [statusRows]
  );

  const statusTotal = useMemo(
    () => statusRows.reduce((s, r) => s + r.count, 0),
    [statusRows]
  );

  const submissionsByStatusOption: EChartsCoreOption = useMemo(() => {
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      // No built-in legend — a vertical HTML legend on the right of the donut
      // gives properly aligned columns and uses the wide card's horizontal
      // space (instead of stacking under the donut).
      series: [
        {
          type: 'pie',
          radius: ['32%', '82%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
          label: {
            show: true,
            position: 'inside',
            formatter: '{c}',
            color: '#ffffff',
            fontSize: 12,
            fontWeight: 600,
          },
          labelLine: { show: false },
          emphasis: {
            label: { show: true, fontSize: 14, fontWeight: 700, formatter: '{b}\n{c} ({d}%)' },
          },
          data: statusSlices.map((s) => ({
            name: s.name,
            value: s.value,
            itemStyle: { color: s.color },
          })),
        },
      ],
    };
  }, [statusSlices]);

  // ====================================================================
  // Excel export
  // ====================================================================
  const handleExport = () => {
    if (!data) return;
    const headlineRows = [
      { Section: 'KPI', Metric: 'In Workflow (Pending)',     Value: data.pendingInWorkflowCount },
      { Section: 'KPI', Metric: 'Awaiting My Action',        Value: data.awaitingMyActionCount },
      { Section: 'KPI', Metric: 'As Final Approver (Pending)', Value: data.finalApproverPendingCount },
      { Section: 'KPI', Metric: 'Decisions Taken',           Value: data.decisionsTakenCount },
      { Section: 'KPI', Metric: 'Requests Submitted',        Value: data.submittedCount },
      { Section: 'KPI', Metric: 'Decisions Received',        Value: data.decisionsReceivedCount },
    ];
    const pendingRowsExcel = data.pendingByRequestType.map((r) => ({
      Section: 'PendingByRequestType',
      RequestType: r.requestTypeName,
      Code: r.requestTypeCode,
      InWorkflow: r.inWorkflowCount,
      AwaitingMyAction: r.awaitingMyActionCount,
      AsFinalApprover: r.finalApproverCount,
    }));
    const actionRowsExcel = data.decisionsByActionType.map((r) => ({
      Section: 'DecisionsByActionType',
      ActionType: r.actionType,
      Count: r.count,
    }));
    const statusRowsExcel = data.submissionsByStatus.map((r) => ({
      Section: 'SubmissionsByStatus',
      Status: r.status,
      Count: r.count,
    }));
    const blockerRowsExcel = data.inWorkflowByBlockingApprover.map((r) => ({
      Section: 'InWorkflowByBlockingApprover',
      BlockingApprover: r.blockerName,
      RequestType: r.requestTypeName,
      Code: r.requestTypeCode,
      PendingCount: r.pendingCount,
      PendingValue: r.pendingValue,
      Currency: r.currency ?? '',
    }));
    const valueByTypeRowsExcel = data.workflowValueByRequestType.map((r) => ({
      Section: 'WorkflowValueByRequestType',
      RequestType: r.requestTypeName,
      Code: r.requestTypeCode,
      TotalValue: r.totalValue,
      Currency: r.currency ?? '',
    }));
    exportToExcel({
      data: [
        ...headlineRows,
        ...pendingRowsExcel,
        ...actionRowsExcel,
        ...statusRowsExcel,
        ...blockerRowsExcel,
        ...valueByTypeRowsExcel,
      ] as any[],
      fileName: `workflow-summary_${new Date().toISOString().slice(0, 10)}.xlsx`,
    });
  };

  // ====================================================================
  // Layout helpers
  // ====================================================================
  const section1ChartsGrid: React.CSSProperties = isNarrow
    ? { display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', marginTop: '0.75rem', minWidth: 0 }
    : {
        display: 'grid',
        // minmax(0, …) so chart cards can shrink below their content's
        // natural min-width — prevents the row from forcing a horizontal
        // page scroll when chart labels are long.
        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
        gap: '1rem',
        marginTop: '0.75rem',
        minWidth: 0,
      };

  const hasPending  = pendingRows.length > 0 && data && data.pendingInWorkflowCount > 0;
  const hasStatuses = statusRows.length > 0;

  return (
    <ReportPageLayout
      title="Workflow Summary"
      description="Where I sit in the system right now — my workflow participation and my own submissions."
      onRefresh={refetch}
      onExport={canExport ? handleExport : undefined}
      exportDisabled={!data}
      loading={loading}
      error={error}
    >
      {/* While the multi-query payload is being assembled on first load, show
          a single spinner instead of letting the user see empty sections that
          progressively fill in. On subsequent refreshes (data already present),
          keep showing the existing values — the spinning Refresh button is
          enough indication that an update is in flight. */}
      {!data && loading && (
        <div style={loadingStateStyle} role="status" aria-live="polite">
          <div style={loadingSpinnerWrapStyle}>
            <IconSystem.refresh size={42} className="spinner" />
          </div>
          <div style={loadingTitleStyle}>Loading Workflow Summary…</div>
          <div style={loadingSubtitleStyle}>
            Aggregating your pending workflow, decisions, and submissions across all request types.
          </div>
        </div>
      )}

      {data && (
        <>

      {/* ================== Section 1: Workflow Participation ================== */}
      {/* No overflowX:hidden here — that escalates overflowY to auto per CSS
          spec and creates an unwanted internal scrollbar. Horizontal
          containment is handled by the grid `minmax(0, …)` templates below
          and the table's own overflowX:auto. */}
      <div style={{ minWidth: 0 }}>
        <h3 style={sectionHeadingStyle}>🔹 Workflow Participation</h3>

        <div style={kpiRowStyleFactory(4, isNarrow)}>
          <KpiCard
            label="In Workflow (Pending)"
            value={data?.pendingInWorkflowCount ?? 0}
            hint="waiting on earlier approvers"
            tone="blue"
            isNarrow={isNarrow}
          />
          <KpiCard
            label="Awaiting My Action"
            value={data?.awaitingMyActionCount ?? 0}
            hint="in my bucket now"
            tone="amber"
            isNarrow={isNarrow}
          />
          <KpiCard
            label="As Final Approver"
            value={data?.finalApproverPendingCount ?? 0}
            hint="final role, pending"
            tone="purple"
            isNarrow={isNarrow}
          />
          <KpiCard
            label="Decisions Taken"
            value={data?.decisionsTakenCount ?? 0}
            hint="all-time"
            tone="green"
            isNarrow={isNarrow}
          />
        </div>

        <div style={section1ChartsGrid}>
          <ChartCard
            title="Pending by Request Type"
            subtitle={
              <div>
                {/* Top row: view-aware status text + view-specific control */}
                <div style={chartSubtitleStyle}>
                  <span>
                    {pendingViewMode === 'chart' && hasPending && (
                      showAllInChart
                        ? `Showing all ${pendingRows.length} type${pendingRows.length === 1 ? '' : 's'} — grouped by 1a / 1b / 1d`
                        : `Showing top ${chartPendingRows.length} of ${pendingRows.length} type${pendingRows.length === 1 ? '' : 's'} (by pending total)`
                    )}
                    {pendingViewMode === 'chart' && !hasPending && 'No items pending in your workflow chain'}
                    {pendingViewMode === 'blocker' && (
                      blockerRows.length > 0
                        ? `${new Set(blockerRows.map((r) => r.blockerId)).size} blocking approver(s) holding up ${data?.pendingInWorkflowCount ?? 0} of my pending request(s) across ${new Set(blockerRows.map((r) => r.requestTypeId)).size} request type(s)`
                        : 'Nothing is blocked — every request in your chain has reached you.'
                    )}
                  </span>
                  {pendingViewMode === 'chart' && pendingRows.length > CHART_TOP_N && (
                    <label style={toggleLabelStyle}>
                      <input
                        type="checkbox"
                        checked={showAllInChart}
                        onChange={(e) => setShowAllInChart(e.target.checked)}
                      />
                      Show all in chart
                    </label>
                  )}
                </div>
                {/* Bottom row: 3-way view segmented control */}
                <div style={subtitleRowStyle}>
                  <div style={segmentedControlStyle} role="tablist" aria-label="Pending view mode">
                    {([
                      { key: 'chart', label: 'By Request Type' },
                      { key: 'blocker', label: 'By Blocking Approver' },
                    ] as Array<{ key: PendingViewMode; label: string }>).map((opt, idx, arr) => (
                      <button
                        key={opt.key}
                        type="button"
                        role="tab"
                        aria-selected={pendingViewMode === opt.key}
                        onClick={() => setPendingViewMode(opt.key)}
                        style={{
                          ...segmentBaseStyle,
                          ...(pendingViewMode === opt.key ? segmentActiveStyle : null),
                          ...(idx === arr.length - 1 ? { borderRight: 'none' } : null),
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            }
            height={
              pendingViewMode === 'chart'
                ? (isNarrow
                    ? Math.min(300, Math.max(220, chartPendingRows.length * 22 + 40))
                    : Math.min(420, Math.max(240, chartPendingRows.length * 28 + 60)))
                : 'auto'
            }
          >
            {pendingViewMode === 'blocker' ? (
              <GroupedPendingList
                rows={blockerRows}
                groupId={(r) => r.blockerId}
                groupName={(r) => r.blockerName}
                innerLabel={(r) => formatTypeName(r.requestTypeName)}
                count={(r) => r.pendingCount}
                value={(r) => r.pendingValue}
                currency={(r) => r.currency}
                emptyMessage="Nothing is blocked — every request in your chain has reached you."
              />
            ) : hasPending ? (
              <EChart option={pendingByTypeOption} height="100%" />
            ) : (
              <EmptyChart message="Nothing pending — clean slate." />
            )}
          </ChartCard>

          <ChartCard
            title="Workflow Value by Request Type"
            subtitle={
              hasValueData ? (
                <div style={chartSubtitleStyle}>
                  <span>
                    {formatValueAbbr(valueTotal)}
                    {valueDominantCurrency ? ' ' + valueDominantCurrency : ' (mixed currencies)'}
                    {' '}across {valueRows.length} request type{valueRows.length === 1 ? '' : 's'} in my workflow
                  </span>
                  <button
                    type="button"
                    onClick={() => setAllTypesModalOpen(true)}
                    style={viewAllLinkStyle}
                  >
                    View all types ↗
                  </button>
                </div>
              ) : (
                'No workflow items with recorded value yet'
              )
            }
            height={
              isNarrow
                ? 360
                : Math.min(480, Math.max(320, chartPendingRows.length * 28 + 120))
            }
          >
            {hasValueData ? (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                  <EChart option={workflowValueByTypeOption} height="100%" />
                </div>
                <div style={legendGridStyleFactory(isNarrow)}>
                  {valueSlices.map((s) => (
                    <div key={s.name} style={legendItemStyle} title={s.name}>
                      <span style={{ ...legendSwatchStyle, background: s.color }} aria-hidden="true" />
                      <span style={legendLabelStyle}>{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyChart message="No workflow items with recorded value yet." />
            )}
          </ChartCard>
        </div>

      </div>

      {/* ================== Section 2: My Submissions ================== */}
      <div style={{ minWidth: 0 }}>
        <h3 style={sectionHeadingStyle}>🔹 My Submissions</h3>

        <div style={kpiRowStyleFactory(2, isNarrow)}>
          <KpiCard
            label="Requests Submitted"
            value={data?.submittedCount ?? 0}
            hint="all-time"
            tone="cyan"
            isNarrow={isNarrow}
          />
          <KpiCard
            label="Decisions Received"
            value={data?.decisionsReceivedCount ?? 0}
            hint="approved / rejected / returned"
            tone="rose"
            isNarrow={isNarrow}
          />
        </div>

        <div style={{ marginTop: '0.75rem' }}>
          <ChartCard
            title="Submissions by Status"
            subtitle={
              hasStatuses
                ? `Breakdown of ${data!.submittedCount} request${data!.submittedCount === 1 ? '' : 's'} I have submitted`
                : "You haven't submitted any requests yet"
            }
            // Narrow card is taller so the column-stacked donut + legend
            // (≈220 + 12 + 6×26 ≈ 388px content) fits without overlapping the title.
            height={isNarrow ? 460 : 380}
          >
            {hasStatuses ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: isNarrow ? 'column' : 'row',
                  height: '100%',
                  minHeight: 0,
                  gap: isNarrow ? '0.75rem' : '2.5rem',
                  // On wide (row layout) centre the donut+legend pair horizontally.
                  // On narrow (column layout) align to the top so the donut never
                  // bleeds into the card title when content exceeds card height.
                  alignItems: 'center',
                  justifyContent: isNarrow ? 'flex-start' : 'center',
                }}
              >
                {/* Donut: bounded box (~300px square on wide) so it doesn't
                    balloon to fill the entire card width. Centred as part of
                    the donut+legend group via the outer flex's justifyContent. */}
                <div
                  style={{
                    flexShrink: 0,
                    width: isNarrow ? '100%' : '300px',
                    maxWidth: '100%',
                    height: isNarrow ? '220px' : '100%',
                    maxHeight: isNarrow ? '220px' : '300px',
                  }}
                >
                  <EChart option={submissionsByStatusOption} height="100%" />
                </div>
                {/* Vertical legend on the right — proper column alignment via
                    CSS grid (swatch | name | count | %). Content-sized so it
                    doesn't claim phantom width. */}
                <div
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minHeight: 0,
                    maxHeight: '100%',
                  }}
                >
                  <div style={sideLegendWrapStyle}>
                    {statusSlices.map((s) => {
                      const pct = statusTotal > 0 ? (s.value / statusTotal) * 100 : 0;
                      return (
                        <React.Fragment key={s.name}>
                          <span
                            style={{ ...sideLegendSwatchStyle, background: s.color }}
                            aria-hidden="true"
                          />
                          <span style={sideLegendNameStyle} title={s.name}>{s.name}</span>
                          <span style={sideLegendCountStyle}>{s.value.toLocaleString()}</span>
                          <span style={sideLegendPctStyle}>{pct.toFixed(1)}%</span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <EmptyChart message="No submissions to chart." />
            )}
          </ChartCard>
        </div>
      </div>
        </>
      )}

      {/* "View all types" modal — lists every request type with value + %.
          Rendered via portal so it escapes any ancestor overflow:hidden. */}
      {allTypesModalOpen && createPortal(
        <AllTypesModal
          rows={valueRows}
          total={valueTotal}
          dominantCurrency={valueDominantCurrency}
          onClose={() => setAllTypesModalOpen(false)}
        />,
        document.body
      )}
    </ReportPageLayout>
  );
};

// ---------------------------------------------------------------------------
// "View all types" modal — full sorted list of every request type
// ---------------------------------------------------------------------------
const AllTypesModal: React.FC<{
  rows: ReadonlyArray<{ requestTypeId: string; requestTypeName: string; totalValue: number; currency?: string | null }>;
  total: number;
  dominantCurrency: string | null;
  onClose: () => void;
}> = ({ rows, total, dominantCurrency, onClose }) => {
  const sorted = useMemo(
    () => rows.slice().sort((a, b) => b.totalValue - a.totalValue),
    [rows]
  );
  const currencyLabel = dominantCurrency ?? '';

  return (
    <div
      style={modalBackdropStyle}
      role="presentation"
    >
      <div
        style={modalContainerStyle}
        role="dialog"
        aria-modal="true"
        aria-label="All request types by workflow value"
      >
        <div style={modalHeaderStyle}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1f2937' }}>
              Workflow Value by Request Type
            </div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.125rem' }}>
              {formatValueAbbr(total)}{currencyLabel ? ' ' + currencyLabel : ' (mixed)'}
              {' '}across {sorted.length} type{sorted.length === 1 ? '' : 's'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={modalCloseButtonStyle}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={modalBodyStyle}>
          {sorted.map((r, idx) => {
            const pct = total > 0 ? (r.totalValue / total) * 100 : 0;
            const swatch = TYPE_VALUE_PALETTE[idx % TYPE_VALUE_PALETTE.length];
            const rowCurrency = r.currency ?? currencyLabel;
            return (
              <div key={r.requestTypeId} style={modalRowStyle}>
                <span style={{ ...modalRowSwatchStyle, background: swatch }} aria-hidden="true" />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formatTypeName(r.requestTypeName)}
                </span>
                <span style={modalValueStyle}>
                  {formatValueAbbr(r.totalValue)}{rowCurrency ? ' ' + rowCurrency : ''}
                </span>
                <span style={modalPctStyle}>{pct.toFixed(1)}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------
const KpiCard: React.FC<{
  label: string;
  value: number;
  hint?: string;
  tone: CardTone;
  isNarrow?: boolean;
}> = ({ label, value, hint, tone, isNarrow }) => {
  const t = cardTones[tone];
  return (
    <div
      style={{
        ...kpiCardStyle,
        padding: isNarrow ? '0.5rem 0.625rem 0.5rem 0.75rem' : '1rem 1.1rem 1rem 1.25rem',
        gap: isNarrow ? '0.125rem' : '0.2rem',
        background: t.background,
        border: `1px solid ${t.border}`,
        borderLeft: `4px solid ${t.accent}`,
        minWidth: 0,
      }}
    >
      <span
        style={{
          ...kpiLabelStyle,
          color: t.label,
          fontSize: isNarrow ? '0.6rem' : '0.7rem',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
      >
        {label}
      </span>
      <span
        style={{
          ...kpiValueStyle,
          color: t.value,
          fontSize: isNarrow ? '1.25rem' : '1.8rem',
        }}
      >
        {value.toLocaleString()}
      </span>
      {hint && !isNarrow && <span style={{ ...kpiHintStyle, color: t.label }}>{hint}</span>}
    </div>
  );
};

const EmptyChart: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#9ca3af',
      fontSize: '0.85rem',
    }}
  >
    {message}
  </div>
);

// ---------------------------------------------------------------------------
// Grouped expandable list (shared by the By-Submitter and By-Blocker views)
// ---------------------------------------------------------------------------

interface GroupedPendingListProps<TRow> {
  rows: TRow[];
  groupId: (r: TRow) => string;
  groupName: (r: TRow) => string;
  innerLabel: (r: TRow) => string;
  count: (r: TRow) => number;
  value: (r: TRow) => number;
  currency: (r: TRow) => string | null | undefined;
  emptyMessage: string;
}

interface PendingGroup<TRow> {
  id: string;
  name: string;
  rows: TRow[];
  totalCount: number;
  totalValue: number;
  currency: string | null;
}

function GroupedPendingList<TRow>({
  rows,
  groupId,
  groupName,
  innerLabel,
  count,
  value,
  currency,
  emptyMessage,
}: GroupedPendingListProps<TRow>) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const allGroups = useMemo<PendingGroup<TRow>[]>(() => {
    const map = new Map<string, PendingGroup<TRow>>();
    for (const r of rows) {
      const id = groupId(r);
      let g = map.get(id);
      if (!g) {
        g = {
          id,
          name: groupName(r),
          rows: [],
          totalCount: 0,
          totalValue: 0,
          currency: currency(r) ?? null,
        };
        map.set(id, g);
      }
      g.rows.push(r);
      g.totalCount += count(r);
      g.totalValue += value(r);
      const rowCurrency = currency(r) ?? null;
      // If any child row's currency disagrees with the group's running currency,
      // mark the group as mixed (null).
      if (g.currency && rowCurrency && g.currency !== rowCurrency) {
        g.currency = null;
      } else if (!g.currency && rowCurrency) {
        g.currency = rowCurrency;
      }
    }
    return [...map.values()].sort((a, b) => b.totalCount - a.totalCount);
  }, [rows, groupId, groupName, count, value, currency]);

  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allGroups;
    return allGroups.filter((g) => g.name.toLowerCase().includes(term));
  }, [allGroups, search]);

  const allExpanded = allGroups.length > 0 && expanded.size === allGroups.length;

  const toggleGroup = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpanded(new Set(allGroups.map((g) => g.id)));
  const collapseAll = () => setExpanded(new Set());

  if (rows.length === 0) {
    return <EmptyChart message={emptyMessage} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…"
          style={searchInputStyle}
          aria-label="Search"
        />
        <div style={{ display: 'flex', gap: '0.375rem' }}>
          <button
            type="button"
            onClick={expandAll}
            disabled={allExpanded}
            style={{
              ...segmentBaseStyle,
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              borderRight: '1px solid #d1d5db',
              background: '#f9fafb',
              opacity: allExpanded ? 0.5 : 1,
              cursor: allExpanded ? 'not-allowed' : 'pointer',
            }}
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={collapseAll}
            disabled={expanded.size === 0}
            style={{
              ...segmentBaseStyle,
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              borderRight: '1px solid #d1d5db',
              background: '#f9fafb',
              opacity: expanded.size === 0 ? 0.5 : 1,
              cursor: expanded.size === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            Collapse all
          </button>
        </div>
      </div>

      <div style={{ maxHeight: 380, overflowY: 'auto', overflowX: 'auto', border: '1px solid #f3f4f6', borderRadius: '0.375rem' }}>
        {visibleGroups.length === 0 ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
            No matches for "{search}".
          </div>
        ) : (
          visibleGroups.map((g) => {
            const isOpen = expanded.has(g.id);
            return (
              <React.Fragment key={g.id}>
                <div
                  style={groupOuterRowStyle}
                  onClick={() => toggleGroup(g.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGroup(g.id);
                    }
                  }}
                  aria-expanded={isOpen}
                >
                  <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {g.name}
                  </span>
                  <span style={groupCountStyle}>{g.totalCount.toLocaleString()}</span>
                  <span style={groupValueStyle}>{formatValue(g.totalValue, g.currency)}</span>
                </div>
                {isOpen &&
                  g.rows.map((row, i) => (
                    <div key={`${g.id}-${i}`} style={groupInnerRowStyle}>
                      <span />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {innerLabel(row)}
                      </span>
                      <span style={groupCountStyle}>{count(row).toLocaleString()}</span>
                      <span style={groupValueStyle}>{formatValue(value(row), currency(row) ?? null)}</span>
                    </div>
                  ))}
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}

const formatValue = (val: number, currency: string | null): string => {
  if (!val) return '';
  // Indian number formatting: X,XX,XXX.XX — matches the deployment context
  // and is consistent with the abbreviated formatter above (Cr / L / K).
  const formatted = val.toLocaleString('en-IN');
  if (currency === null) return `${formatted} (mixed)`;
  return `${formatted} ${currency}`;
};

// Compact value formatter for chart labels (tooltips, centre text, slice labels).
// Keeps long monetary numbers from overflowing the donut's tight space.
// Uses the Indian numbering scale (Crore / Lakh / Thousand) — the canonical
// way to compress monetary amounts in DAS's deployment context.
//   1 Crore (Cr) = 1,00,00,000 (10^7)
//   1 Lakh  (L)  = 1,00,000    (10^5)
//   1 K          = 1,000       (10^3)
const formatValueAbbr = (val: number): string => {
  if (!val) return '0';
  const abs = Math.abs(val);
  if (abs >= 10_000_000) return `${(val / 10_000_000).toFixed(2)} Cr`;
  if (abs >= 100_000)    return `${(val / 100_000).toFixed(2)} L`;
  if (abs >= 10_000)     return `${(val / 1_000).toFixed(1)} K`;
  return val.toLocaleString('en-IN');
};

// Normalises request type names for display. The source table has mixed casing
// ("AUCTION EXPENDITURE" vs "Account Head Creation" vs "TDS Payments"). This
// title-cases every word EXCEPT short all-uppercase tokens (≤ 4 chars), which
// are preserved as acronyms so TDS/NEFT/GST/OD stay shouty and meaningful.
const formatTypeName = (raw: string): string => {
  if (!raw) return raw;
  return raw
    .split(/\s+/)
    .map((token) => {
      // Match leading/trailing punctuation around the alphanumeric core
      // (handles "(DEVIATION)", "REQUESTS,", "ITEM.").
      const m = token.match(/^([^A-Za-z0-9]*)([A-Za-z0-9][A-Za-z0-9'-]*)([^A-Za-z0-9]*)$/);
      if (!m) return token;
      const [, leading, core, trailing] = m;
      const isAcronym = core.length <= 4 && /^[A-Z]+$/.test(core);
      const cased = isAcronym
        ? core
        : core.charAt(0).toUpperCase() + core.slice(1).toLowerCase();
      return leading + cased + trailing;
    })
    .join(' ');
};

export default WorkflowSummaryReport;
